/**
 * ChangelogList — the "This week" tab.
 *
 * Renders the user's resolved reports grouped by ISO week of resolution
 * date, most recent week first. Trust-building surface borrowed from
 * thePnr's `FeedbackChangelogView` ("see what's been fixed"). Polls every
 * 30 s while open, same cadence as MineList.
 *
 * Scope is the submitter's own resolved reports only — multi-tenant
 * privacy default. A per-project "show all-project changelog to
 * customers" setting can expand the scope in a later release.
 */

import { h } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'

import type { ApiClient } from '../api/client'
import type { WidgetChangelogRow } from '../types'
import type { StringKey } from './i18n'
import { ReportRow } from './ReportRow'

interface ChangelogListProps {
  api: ApiClient
  externalId: string
  strings: Record<StringKey, string>
  onSelect: (report: WidgetChangelogRow) => void
}

const POLL_MS = 30_000

interface WeekGroup {
  /** ISO date (YYYY-MM-DD) of the Monday anchoring this week. */
  weekKey: string
  /** Localized "Week of {Mon DD, YYYY}" label, computed once per group. */
  label: string
  rows: WidgetChangelogRow[]
}

/**
 * Week-of-resolution key for a row. Anchored on Monday in UTC so the
 * grouping is consistent across timezones — the user-visible label is
 * localized at render time.
 */
export function isoWeekKey(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // JS Sunday=0; we want Monday=0 to anchor weeks the way thePnr does.
  const day = (d.getUTCDay() + 6) % 7
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day))
  return monday.toISOString().slice(0, 10)
}

export function groupRowsByWeek(rows: WidgetChangelogRow[]): WeekGroup[] {
  const buckets = new Map<string, WidgetChangelogRow[]>()
  for (const row of rows) {
    const key = isoWeekKey(row.resolved_at)
    if (!key) continue
    const existing = buckets.get(key)
    if (existing) existing.push(row)
    else buckets.set(key, [row])
  }
  // Sort buckets by week descending (most recent week first); within each
  // bucket the server already returned -resolved_at order.
  return Array.from(buckets.entries())
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([weekKey, weekRows]) => ({
      weekKey,
      label: formatWeekLabel(weekKey),
      rows: weekRows,
    }))
}

function formatWeekLabel(weekKey: string): string {
  // Render just the date portion ("May 5, 2026"); the i18n template
  // wraps it as "Week of {date}".
  try {
    return new Date(weekKey).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return weekKey
  }
}

export function ChangelogList({ api, externalId, strings, onSelect }: ChangelogListProps) {
  const [rows, setRows] = useState<WidgetChangelogRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const mountedRef = useRef(true)

  const fetchRows = async () => {
    setRefreshing(true)
    setError(null)
    try {
      const next = await api.listChangelog(externalId)
      if (!mountedRef.current) return
      setRows(next)
    } catch (err) {
      // Friendly UI fallback; raw error to console for the developer.
      if (typeof console !== 'undefined')
        console.warn('[mhosaic] listChangelog:', err)
      if (!mountedRef.current) return
      setError(strings['mine.error'])
    } finally {
      if (mountedRef.current) setRefreshing(false)
    }
  }

  useEffect(() => {
    mountedRef.current = true
    void fetchRows()
    const timer = setInterval(() => {
      void fetchRows()
    }, POLL_MS)
    return () => {
      mountedRef.current = false
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId])

  const groups = useMemo(() => (rows ? groupRowsByWeek(rows) : []), [rows])
  const isLoading = rows === null && !error
  const isEmpty = rows !== null && rows.length === 0

  return (
    <div class="mine-list">
      <div class="mine-list-header">
        <h2>{strings['tab.changelog']}</h2>
        <button
          type="button"
          class="btn"
          onClick={() => {
            void fetchRows()
          }}
          disabled={refreshing}
        >
          {refreshing ? strings['mine.loading'] : strings['mine.refresh']}
        </button>
      </div>
      {isLoading && <div class="mine-loading">{strings['mine.loading']}</div>}
      {error && <div class="error">{error}</div>}
      {isEmpty && (
        <div class="mine-empty">
          <strong>{strings['changelog.empty.title']}</strong>
          <p>{strings['changelog.empty.body']}</p>
        </div>
      )}
      {groups.length > 0 && (
        <div class="changelog-groups">
          {groups.map((g) => (
            <section class="changelog-group" key={g.weekKey}>
              <header class="changelog-group-header">
                <span class="changelog-group-marker" aria-hidden="true">●</span>
                <span class="changelog-group-label">
                  {strings['changelog.week_of'].replace('{date}', g.label)}
                </span>
                <span class="changelog-group-rule" aria-hidden="true" />
                <span class="changelog-group-count">
                  {strings[
                    g.rows.length === 1 ? 'changelog.resolved_one' : 'changelog.resolved_many'
                  ].replace('{count}', String(g.rows.length))}
                </span>
              </header>
              <ul class="mine-rows">
                {g.rows.map((row) => (
                  <li>
                    <ReportRow row={row} strings={strings} onClick={() => onSelect(row)} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
